// Cámara dentro de la app: siempre en vivo (no se puede elegir de la galería ni de archivos); linterna si el equipo la tiene.
// Se abre con abrirCamara({ multiple }) → Promise<File[]> (vacío si se cancela). <CamaraHost /> va una sola vez en App.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Check, RefreshCw, X, Zap, ZapOff, Loader2 } from 'lucide-react';

let abrirGlobal = null;
export const abrirCamara = ({ multiple = false } = {}) =>
  new Promise((resolve) => (abrirGlobal ? abrirGlobal({ multiple, resolve }) : resolve([])));

const ERRORES = {
  NotAllowedError: 'Permití el acceso a la cámara en el navegador para tomar fotos.',
  NotFoundError: 'No se encontró una cámara en este equipo.',
  NotReadableError: 'La cámara está ocupada por otra aplicación. Cerrala e intentá de nuevo.',
};

const capturar = (video) => new Promise((resolve) => {
  const c = document.createElement('canvas');
  c.width = video.videoWidth;
  c.height = video.videoHeight;
  c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
  c.toBlob((b) => resolve(b ? new File([b], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' }) : null), 'image/jpeg', 0.92);
});

const useStream = (videoRef, frente) => {
  const [track, setTrack] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let stream = null;
    let vivo = true;
    setTrack(null);
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) { setError('Este navegador no permite usar la cámara.'); return undefined; }
    navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: frente ? 'user' : 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } } })
      .then((s) => {
        if (!vivo) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
        setTrack(s.getVideoTracks()[0] || null);
      })
      .catch((e) => vivo && setError(ERRORES[e?.name] || 'No se pudo abrir la cámara.'));
    return () => { vivo = false; stream?.getTracks().forEach((t) => t.stop()); };
  }, [videoRef, frente]);
  return { track, error };
};

const BotonRedondo = ({ onClick, children, testid, label, activo }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testid}
    className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors active:scale-95 ${activo ? 'bg-yellow-400 text-black' : 'bg-white/15 text-white hover:bg-white/25'}`}>
    {children}
  </button>
);

const CamaraVista = ({ multiple, onListo }) => {
  const videoRef = useRef(null);
  const [frente, setFrente] = useState(false);
  const { track, error } = useStream(videoRef, frente);
  const [listo, setListo] = useState(false);
  useEffect(() => { setListo(false); }, [track]);
  const [flash, setFlash] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [destello, setDestello] = useState(false);
  const conFlash = !!track?.getCapabilities?.()?.torch;

  useEffect(() => { setFlash(false); }, [track]);
  const alternarFlash = () => {
    const v = !flash;
    track.applyConstraints({ advanced: [{ torch: v }] }).then(() => setFlash(v)).catch(() => {});
  };
  const disparar = useCallback(async () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    setDestello(true);
    setTimeout(() => setDestello(false), 120);
    const f = await capturar(v);
    if (!f) return;
    if (!multiple) onListo([f]);
    else setFotos((x) => [...x, f]);
  }, [multiple, onListo]);

  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-black flex flex-col pointer-events-auto" data-testid="camara-en-vivo" data-camara-en-vivo=""
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onListo([]); } }}>
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-contain" data-testid="camara-video"
          onLoadedData={(e) => setListo(e.currentTarget.videoWidth > 0)} onPlaying={(e) => setListo(e.currentTarget.videoWidth > 0)} />
        {destello && <div className="absolute inset-0 bg-white/70" />}
        {(!track || !listo) && !error && <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Abriendo cámara…</div>}
        {error && <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white text-sm" data-testid="camara-error">{error}</div>}
        <button type="button" onClick={() => onListo([])} aria-label="Cerrar cámara" data-testid="camara-cerrar"
          className="absolute top-3 right-3 h-10 w-10 rounded-full bg-black/50 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
        {multiple && fotos.length > 0 && <div className="absolute top-3 left-3 rounded-full bg-black/60 px-3 py-1 text-xs text-white" data-testid="camara-contador">{fotos.length} foto{fotos.length === 1 ? '' : 's'}</div>}
      </div>
      <div className="flex items-center justify-around px-6 py-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        {conFlash ? (
          <BotonRedondo onClick={alternarFlash} testid="camara-flash" label="Flash" activo={flash}>{flash ? <Zap className="h-5 w-5" /> : <ZapOff className="h-5 w-5" />}</BotonRedondo>
        ) : <span className="h-12 w-12" />}
        <button type="button" onClick={disparar} disabled={!track || !listo} aria-label="Tomar foto" data-testid="camara-disparar"
          className="h-[72px] w-[72px] rounded-full border-4 border-white bg-white/90 flex items-center justify-center active:scale-95 disabled:opacity-40">
          <Camera className="h-7 w-7 text-gray-800" />
        </button>
        {multiple && fotos.length > 0 ? (
          <BotonRedondo onClick={() => onListo(fotos)} testid="camara-listo" label="Listo" activo><Check className="h-6 w-6" /></BotonRedondo>
        ) : (
          <BotonRedondo onClick={() => setFrente((x) => !x)} testid="camara-girar" label="Cambiar cámara"><RefreshCw className="h-5 w-5" /></BotonRedondo>
        )}
      </div>
    </div>,
    document.body,
  );
};

export const CamaraHost = () => {
  const [pedido, setPedido] = useState(null);
  useEffect(() => { abrirGlobal = setPedido; return () => { abrirGlobal = null; }; }, []);
  if (!pedido) return null;
  return <CamaraVista multiple={pedido.multiple} onListo={(files) => { pedido.resolve(files); setPedido(null); }} />;
};
